/* Goldie — Guide Article: "Listen to this article" player.
   Custom element <goldie-audio-player>. Wraps the widget markup rendered by
   snippets/goldie-audio-player.liquid and drives two mutually exclusive modes:
     - hosted MP3  (data-audio-src, from article.metafields.custom.audio_file)
     - browser SpeechSynthesis fallback, reading the editorial content aloud
   The floating "mini" bar is built and owned entirely by this component, so
   no other template needs to know about it.
   Loaded with defer; defines the element, no other global side effects. */

class GoldieAudioPlayer extends HTMLElement {
  connectedCallback() {
    this.article = document.querySelector('article');
    this.toggleBtn = this.querySelector('#audioToggle');
    this.fill = this.querySelector('#audioFill');
    this.timeEl = this.querySelector('#audioTime');
    this.rateBtn = this.querySelector('#audioRate');
    this.track = this.querySelector('.audio-track');
    /* audio-note is a sibling, not a descendant — it sits outside this
       element in the markup on purpose, matching the prototype (no card
       background around the disclaimer line), so it can't use this.querySelector. */
    this.note = document.getElementById('audioNote');

    if (!this.article || !this.toggleBtn || !this.fill || !this.timeEl || !this.rateBtn || !this.track || !this.note) {
      return;
    }

    this.audioSrc = this.dataset.audioSrc || null;
    this.rates = [1, 1.25, 1.5, 0.85];
    this.rateIndex = 0;
    this.rate = 1;
    this.playing = false;

    this.#buildMiniBar();

    if (this.audioSrc) {
      this.#initHostedAudio();
    } else if ('speechSynthesis' in window) {
      this.#initSpeechSynthesis();
    } else {
      this.hidden = true;
    }
  }

  /* ---------- shared UI ---------- */

  #buildMiniBar() {
    const mini = document.createElement('div');
    mini.className = 'miniplayer';
    mini.innerHTML = `
      <button class="mp-btn" type="button" aria-label="Play or pause article audio">
        <svg class="ic-play" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7L8 5Z"/></svg>
        <svg class="ic-pause" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4h4v16H6V4Zm8 0h4v16h-4V4Z"/></svg>
      </button>
      <div class="mp-t">Listening to this article</div>
      <div class="mp-track"><div class="mp-fill"></div></div>
      <button class="mp-x" type="button" aria-label="Stop audio">&times;</button>
    `;
    document.body.appendChild(mini);

    this.mini = mini;
    this.miniToggle = mini.querySelector('.mp-btn');
    this.miniFill = mini.querySelector('.mp-fill');
    this.miniClose = mini.querySelector('.mp-x');
  }

  #fmt(seconds) {
    const s = Math.max(0, Math.round(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  #setProgress(pct, elapsed, total) {
    this.fill.style.width = `${pct}%`;
    this.miniFill.style.width = `${pct}%`;
    this.track.setAttribute('aria-valuenow', String(Math.round(pct)));
    this.timeEl.textContent = `${this.#fmt(elapsed)} / ~${this.#fmt(total)}`;
  }

  #setPlaying(on) {
    this.playing = on;
    this.classList.toggle('playing', on);
    this.mini.classList.toggle('playing', on);
    this.toggleBtn.setAttribute('aria-label', on ? 'Pause article audio' : 'Play article audio');
    if (on) {
      this.mini.classList.add('show');
      document.body.classList.add('mini-on');
    }
  }

  #hideMini() {
    this.mini.classList.remove('show');
    document.body.classList.remove('mini-on');
  }

  #cycleRate(onChange) {
    this.rateIndex = (this.rateIndex + 1) % this.rates.length;
    this.rate = this.rates[this.rateIndex];
    this.rateBtn.textContent = `${this.rate}×`;
    onChange(this.rate);
  }

  /* ---------- mode: hosted MP3 file ---------- */

  #initHostedAudio() {
    const audio = new Audio(this.audioSrc);
    audio.preload = 'metadata';
    this.hidden = false;
    this.note.hidden = false;
    this.note.textContent = 'Narrated version of this article.';

    audio.addEventListener('timeupdate', () => {
      const total = audio.duration || 0;
      this.#setProgress(total ? (audio.currentTime / total) * 100 : 0, audio.currentTime, total);
    });
    audio.addEventListener('ended', () => {
      this.#setPlaying(false);
      this.#hideMini();
    });

    const toggle = () => {
      if (audio.paused) {
        audio.playbackRate = this.rate;
        audio.play();
        this.#setPlaying(true);
      } else {
        audio.pause();
        this.#setPlaying(false);
      }
    };

    this.toggleBtn.addEventListener('click', toggle);
    this.miniToggle.addEventListener('click', toggle);
    this.miniClose.addEventListener('click', () => {
      audio.pause();
      audio.currentTime = 0;
      this.#setPlaying(false);
      this.#hideMini();
    });
    this.rateBtn.addEventListener('click', () => {
      this.#cycleRate((rate) => { audio.playbackRate = rate; });
    });
  }

  /* ---------- mode: browser text-to-speech ---------- */

  #initSpeechSynthesis() {
    const synth = window.speechSynthesis;
    const SKIP = '.pblock, .pinline, .bundle, .pgrid, .toc, .byline, .updated, .audio, figcaption, .disclaimer, .sources, .related, .authorbio, aside, .newsletter, footer, .buybar, .miniplayer';
    const SEL = 'h1, .standfirst, h2, h3, h4, p, li, summary, .faq .ans p';

    const nodes = Array.from(this.article.querySelectorAll(SEL)).filter((el) => {
      if (el.closest(SKIP)) return false;
      if (el.hasAttribute('data-audio-skip')) return false;
      return el.textContent.trim().length > 1;
    });

    const words = nodes.reduce((n, el) => n + el.textContent.trim().split(/\s+/).length, 0);
    const totalSecs = () => (words / (185 * this.rate)) * 60;

    let index = 0;
    let elapsedSecs = 0;
    let tickId = null;
    let activeEl = null;
    let voice = this.#pickVoice(synth);
    if (synth.onvoiceschanged !== undefined) {
      synth.addEventListener('voiceschanged', () => { voice = this.#pickVoice(synth); });
    }

    const clearHighlight = () => {
      if (activeEl) { activeEl.classList.remove('ar-active'); activeEl = null; }
    };
    const highlight = (el) => {
      clearHighlight();
      activeEl = el;
      el.classList.add('ar-active');
      const details = el.closest('details');
      if (details && !details.open) details.open = true;
      const rect = el.getBoundingClientRect();
      if (rect.top < 100 || rect.bottom > window.innerHeight - 120) {
        window.scrollTo({ top: window.scrollY + rect.top - window.innerHeight / 2.6, behavior: 'smooth' });
      }
    };
    const updateProgress = () => {
      const total = totalSecs();
      const pct = total ? Math.min(100, (elapsedSecs / total) * 100) : 0;
      this.#setProgress(pct, elapsedSecs, total);
    };

    /* Ticks the elapsed-time clock every 250ms while playing — real wall-clock
       time, independent of paragraph length, so the counter never freezes
       mid-paragraph and jumps only at the end. */
    const startTicking = () => {
      clearInterval(tickId);
      tickId = setInterval(() => {
        elapsedSecs = Math.min(totalSecs(), elapsedSecs + 0.25);
        updateProgress();
      }, 250);
    };
    const stopTicking = () => clearInterval(tickId);

    const speak = (i) => {
      if (i >= nodes.length) { stop(true); return; }
      index = i;
      const el = nodes[i];
      const text = el.textContent.replace(/\s+/g, ' ').trim();
      highlight(el);
      const utterance = new SpeechSynthesisUtterance(text);
      if (voice) utterance.voice = voice;
      utterance.rate = this.rate;
      utterance.pitch = 1;
      utterance.lang = (voice && voice.lang) || 'en-US';
      utterance.onend = () => {
        if (!this.playing) return;
        /* brief beat after headings for natural pacing */
        setTimeout(() => { if (this.playing) speak(i + 1); }, /^H[1-4]$/.test(el.tagName) ? 260 : 90);
      };
      utterance.onerror = (e) => {
        if (e.error !== 'interrupted' && e.error !== 'canceled' && this.playing) speak(i + 1);
      };
      synth.speak(utterance);
    };

    let watchdogId = null;
    /* Chrome stops synthesis after ~15s of continuous speech; nudge it. */
    const watchdog = () => {
      clearInterval(watchdogId);
      watchdogId = setInterval(() => {
        if (!this.playing) { clearInterval(watchdogId); return; }
        if (synth.speaking && !synth.paused) { synth.pause(); synth.resume(); }
      }, 9000);
    };

    const start = () => { this.#setPlaying(true); synth.cancel(); speak(index); watchdog(); startTicking(); };
    const pause = () => { this.#setPlaying(false); synth.cancel(); clearHighlight(); stopTicking(); };
    const stop = (done) => {
      this.#setPlaying(false); synth.cancel(); clearHighlight(); stopTicking();
      index = 0;
      elapsedSecs = done ? totalSecs() : 0;
      updateProgress();
      this.#hideMini();
    };
    const toggle = () => { this.playing ? pause() : start(); };

    this.toggleBtn.addEventListener('click', toggle);
    this.miniToggle.addEventListener('click', toggle);
    this.miniClose.addEventListener('click', () => stop(false));
    this.rateBtn.addEventListener('click', () => {
      this.#cycleRate(() => {
        updateProgress();
        if (this.playing) { synth.cancel(); speak(index); }
      });
    });
    window.addEventListener('beforeunload', () => synth.cancel());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.playing) pause();
    });

    this.hidden = false;
    this.note.hidden = false;
    updateProgress();
  }

  #pickVoice(synth) {
    const voices = synth.getVoices();
    if (!voices.length) return null;
    const preferred = [
      'Google US English',
      'Samantha',
      'Microsoft Aria Online (Natural) - English (United States)',
      'Microsoft Jenny Online (Natural) - English (United States)',
      'Karen',
      'Alex',
    ];
    for (const name of preferred) {
      const match = voices.find((v) => v.name === name);
      if (match) return match;
    }
    return voices.find((v) => /^en[-_]US/i.test(v.lang)) || voices.find((v) => /^en/i.test(v.lang)) || voices[0];
  }
}

customElements.define('goldie-audio-player', GoldieAudioPlayer);
